export const MAX_CLIP_SECONDS = 60;
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;
export const VIDEO_INSPECTION_TIMEOUT_MS = 15_000;

export type VideoSelection = {
  durationSeconds: number;
  startSeconds: number;
  endSeconds: number;
};

function validDuration(value: number): number {
  if (!Number.isFinite(value) || value <= 0) throw new Error("invalid_video_duration");
  return value;
}

export async function firstValidVideoDuration(
  readers: Array<() => Promise<number>>,
  timeoutMs = VIDEO_INSPECTION_TIMEOUT_MS,
): Promise<number> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error("video_inspection_timeout")), timeoutMs);
  });
  try {
    return await Promise.race([
      Promise.any(readers.map(async (read) => validDuration(await read()))),
      timeout,
    ]);
  } catch {
    throw new Error("Não foi possível ler este vídeo. Tente outro arquivo MP4, MOV ou WebM.");
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

async function inspectWithBrowserMetadata(file: File, timeoutMs: number): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);
    let settled = false;
    const finish = (result: { duration?: number; error?: Error }) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(objectUrl);
      if (result.error) reject(result.error);
      else resolve(validDuration(result.duration ?? 0));
    };
    const timeoutId = setTimeout(
      () => finish({ error: new Error("browser_metadata_timeout") }),
      timeoutMs,
    );
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => finish({ duration: video.duration });
    video.onerror = () => finish({ error: new Error("browser_metadata_failed") });
    video.src = objectUrl;
    video.load();
  });
}

async function inspectWithContainerParser(file: File): Promise<number> {
  const { ALL_FORMATS, BlobSource, Input } = await import("mediabunny");
  const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
  try {
    return validDuration(await input.computeDuration());
  } finally {
    input.dispose();
  }
}

export async function inspectVideo(file: File): Promise<number> {
  return firstValidVideoDuration([
    () => inspectWithBrowserMetadata(file, VIDEO_INSPECTION_TIMEOUT_MS),
    () => inspectWithContainerParser(file),
  ]);
}

export async function prepareVideoClip(
  source: File,
  selection: VideoSelection,
  onProgress: (progress: number) => void,
): Promise<File> {
  const clipDuration = selection.endSeconds - selection.startSeconds;
  if (clipDuration <= 0 || clipDuration > MAX_CLIP_SECONDS + 0.01) {
    throw new Error("Selecione um trecho de até 60 segundos.");
  }

  const needsTrim = selection.durationSeconds > MAX_CLIP_SECONDS + 0.01
    || selection.startSeconds > 0.01
    || selection.endSeconds < selection.durationSeconds - 0.01;

  if (!needsTrim) {
    onProgress(1);
    return source;
  }

  const { ALL_FORMATS, BlobSource, BufferTarget, Conversion, Input, Mp4OutputFormat, Output } = await import("mediabunny");
  const input = new Input({ source: new BlobSource(source), formats: ALL_FORMATS });
  const target = new BufferTarget();
  const output = new Output({
    format: new Mp4OutputFormat({ fastStart: "in-memory" }),
    target,
  });

  try {
    const conversion = await Conversion.init({
      input,
      output,
      tracks: "primary",
      trim: { start: selection.startSeconds, end: selection.endSeconds },
    });
    if (!conversion.isValid) {
      const reasons = conversion.discardedTracks.map((item) => item.reason).join(", ");
      throw new Error(`Este vídeo não pode ser cortado neste navegador${reasons ? `: ${reasons}` : "."}`);
    }
    conversion.onProgress = (progress) => onProgress(progress);
    await conversion.execute();
    if (!target.buffer) throw new Error("O corte local não produziu um arquivo válido.");

    const baseName = source.name.replace(/\.[^.]+$/, "") || "video";
    return new File([target.buffer], `${baseName}-ras-clip.mp4`, {
      type: "video/mp4",
      lastModified: Date.now(),
    });
  } finally {
    input.dispose();
  }
}
